import { useState } from "react";
import { MessageCircle, Phone, Send, CalendarCheck } from "lucide-react";
import { Chapter, SectionTitle, Reveal } from "./Reveal";
import { BOOKING_URL, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_NUMBER, WHATSAPP_URL, trackEvent, fbqContact, gaEvent } from "@/data/content";

const inputCls =
  "w-full bg-[#0D0C0B] border border-[#F7F4EE]/15 focus:border-[#C5A059] text-[#F7F4EE] px-4 py-3.5 text-sm outline-none transition-colors duration-300 placeholder:text-[#C5BCAD]/50";

const Reservation = () => {
  const [form, setForm] = useState({ date: "", time: "20:00", guests: "2", notes: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const msg = [
      "Ciao John Tartarino, vorrei prenotare un tavolo:",
      `• Data: ${form.date || "da definire"}`,
      `• Orario: ${form.time}`,
      `• Persone: ${form.guests}`,
      form.notes ? `• Note: ${form.notes}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    fbqContact();
    trackEvent("prenota_click");
    gaEvent("click_whatsapp", { posizione: "form" });
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <section id="prenota" data-testid="reservation-section" className="relative py-24 sm:py-36 bg-[#141210]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Chapter number="10" title="Prenota" />
          <SectionTitle>
            La tua serata
            <span className="italic text-[#C5A059]"> comincia qui.</span>
          </SectionTitle>
          <Reveal delay={0.15}>
            <p className="mt-8 text-base sm:text-lg text-[#C5BCAD] leading-relaxed">
              Via Martiri della Niccioleta 13, Follonica. Dal lunedì al sabato,
              19:30–22:30. Domenica chiuso. Scrivici su WhatsApp: è il modo più
              veloce. Risponde una persona, non un sistema automatico. Per tutto il
              resto — gruppi, occasioni particolari, richieste che meritano due
              righe in più — scrivi a{" "}
              <a href="mailto:info@johntartarino.it" className="text-[#C5A059] hover:text-[#F7F4EE] transition-colors">
                info@johntartarino.it
              </a>
              .
            </p>
          </Reveal>
          <Reveal delay={0.25} className="mt-10 space-y-4">
            <a
              data-testid="reservation-whatsapp-direct"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { fbqContact(); trackEvent("prenota_click"); gaEvent("click_whatsapp", { posizione: "prenota" }); }}
              className="flex items-center gap-4 border border-[#C5A059]/50 bg-[#7A1C2E] p-5 hover:bg-[#9E2A3F] transition-colors duration-300"
            >
              <MessageCircle size={22} strokeWidth={1.5} className="text-[#F7F4EE]" />
              <div>
                <span className="block text-[11px] uppercase tracking-[0.25em] text-[#F7F4EE]/70">WhatsApp — il più veloce</span>
                <span className="block mt-1 font-display text-lg text-[#F7F4EE]">{PHONE_DISPLAY}</span>
              </div>
            </a>
            <a
              data-testid="reservation-booking-online"
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("prenota_click")}
              className="flex items-center gap-4 border border-[#F7F4EE]/10 bg-[#1C1917] p-5 hover:border-[#C5A059]/50 transition-colors duration-300"
            >
              <CalendarCheck size={22} strokeWidth={1.5} className="text-[#C5A059]" />
              <div>
                <span className="block text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD]">Prenota online</span>
                <span className="block mt-1 font-display text-lg text-[#F7F4EE]">Scegli giorno e tavolo in un minuto</span>
              </div>
            </a>
            <a
              data-testid="reservation-phone-direct"
              href={PHONE_TEL}
              className="flex items-center gap-4 border border-[#F7F4EE]/10 bg-[#1C1917] p-5 hover:border-[#C5A059]/50 transition-colors duration-300"
            >
              <Phone size={22} strokeWidth={1.5} className="text-[#C5A059]" />
              <div>
                <span className="block text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD]">Telefono</span>
                <span className="block mt-1 font-display text-lg text-[#F7F4EE]">{PHONE_DISPLAY}</span>
              </div>
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-7">
          <form
            data-testid="reservation-form"
            onSubmit={submit}
            className="border border-[#C5A059]/25 bg-[#1C1917] p-7 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="res-date" className="block text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD] mb-2">
                  Data
                </label>
                <input
                  id="res-date"
                  data-testid="reservation-date-input"
                  type="date"
                  required
                  value={form.date}
                  onChange={set("date")}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
              <div>
                <label htmlFor="res-time" className="block text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD] mb-2">
                  Orario
                </label>
                <select
                  id="res-time"
                  data-testid="reservation-time-select"
                  value={form.time}
                  onChange={set("time")}
                  className={inputCls}
                >
                  {["19:30", "20:00", "20:30", "21:00", "21:30", "22:00"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="res-guests" className="block text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD] mb-2">
                  Numero di persone
                </label>
                <select
                  id="res-guests"
                  data-testid="reservation-guests-select"
                  value={form.guests}
                  onChange={set("guests")}
                  className={inputCls}
                >
                  {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <option key={n} value={n}>{n} persone</option>
                  ))}
                  <option value="9+">Più di 8 (tavolata / occasione speciale)</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="res-notes" className="block text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD] mb-2">
                  Note — occasioni speciali, allergie, richieste
                </label>
                <textarea
                  id="res-notes"
                  data-testid="reservation-notes-textarea"
                  rows={4}
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Es. anniversario, tavolo in dehors, intolleranze..."
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>
            <button
              data-testid="reservation-submit-button"
              type="submit"
              className="mt-8 w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#7A1C2E] text-[#F7F4EE] text-sm uppercase tracking-[0.2em] hover:bg-[#9E2A3F] transition-colors duration-300"
            >
              <Send size={16} />
              Invia richiesta su WhatsApp
            </button>
            <p className="mt-4 text-center text-xs text-[#C5BCAD]/60">
              Il messaggio si apre direttamente in WhatsApp: nessun dato salvato.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export const StickyWhatsApp = () => (
  <a
    data-testid="sticky-whatsapp-mobile-btn"
    href={WHATSAPP_URL}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => { fbqContact(); trackEvent("prenota_click"); gaEvent("click_whatsapp", { posizione: "sticky" }); }}
    aria-label="Prenota su WhatsApp"
    className="fixed bottom-5 right-5 z-50 flex items-center gap-2.5 bg-[#7A1C2E] text-[#F7F4EE] pl-4 pr-5 py-3.5 shadow-2xl shadow-black/50 border border-[#C5A059]/30 hover:bg-[#9E2A3F] transition-colors duration-300"
  >
    <MessageCircle size={20} strokeWidth={1.75} />
    <span className="text-xs uppercase tracking-[0.2em]">Prenota</span>
  </a>
);

export default Reservation;
